import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  Collapse,
  Alert,
  Button,
  Chip,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import uploadTracker from '../services/uploadTracker';

const GlobalUploadStatus = () => {
  const [uploadState, setUploadState] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  console.log('[GlobalUploadStatus] Component rendered');

  useEffect(() => {
    // Check for existing upload state on mount
    const currentState = uploadTracker.getState();

    if (currentState && currentState.status === 'uploading') {

      // Check if upload was started recently (within 30 minutes)
      const timeSinceStart = Date.now() - currentState.startTime;
      const thirtyMinutes = 30 * 60 * 1000;

      if (timeSinceStart < thirtyMinutes) {
        // Restore the upload state but mark it as "reconnecting"
        const restoredState = {
          ...currentState,
          progress: currentState.progress || 50, // Show generic progress
          reconnected: true // Flag to show different UI
        };
        setUploadState(restoredState);
        setVisible(true);
        // Don't auto-expand on restore - let user click to expand
      } else {
        // Upload is too old, clear it
        uploadTracker.clearUpload();
      }
    }

    // Subscribe to upload state changes
    const unsubscribe = uploadTracker.subscribe((state) => {
      setUploadState(state);

      if (state && state.status === 'uploading') {
        setVisible(true);
        // Don't auto-expand - let user click to expand
      } else if (state && (state.status === 'completed' || state.status === 'failed')) {
        setVisible(true);
        // Only auto-expand for completed/failed to show results
        setExpanded(true);
      } else if (!state) {
        setVisible(false);
        setExpanded(false);
      }
    });

    return unsubscribe;
  }, []); // Empty dependency array - only run once on mount

  // Polling effect for reconnected uploads
  useEffect(() => {
    if (!uploadState || uploadState.status !== 'uploading' || !uploadState.reconnected) {
      return;
    }

    // Poll every 10 seconds to check if we can detect upload completion
    const pollInterval = setInterval(async () => {
      try {
        // Check if we're still on the bulk upload page to avoid unnecessary requests
        if (!window.location.pathname.includes('bulk-upload')) {
          return;
        }

        // Simple heuristic: if enough time has passed, assume upload might be complete
        // This is not perfect but provides better UX than showing indefinite progress
        const timeSinceStart = Date.now() - uploadState.startTime;
        const fifteenMinutes = 15 * 60 * 1000;

        if (timeSinceStart > fifteenMinutes) {
          // Upload has been running for a long time, suggest checking manually
          // Could potentially clear the upload or show different UI here
        }
      } catch (error) {
        console.error('[GlobalUploadStatus] Polling error:', error);
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [uploadState]);

  const handleClose = () => {
    uploadTracker.clearUpload();
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploading': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading': return <UploadIcon />;
      case 'completed': return <SuccessIcon />;
      case 'failed': return <ErrorIcon />;
      default: return <UploadIcon />;
    }
  };

  // Debug logging
  console.log('[GlobalUploadStatus] Render check:', {
    visible,
    uploadState,
    status: uploadState?.status
  });

  if (!visible || !uploadState) {
    return null;
  }

  const {
    status,
    progress,
    totalFiles,
    processedFiles,
    currentBatch,
    totalBatches,
    documentType,
    uploadMode,
    startTime,
    endTime,
    results,
    error,
    warnings
  } = uploadState;

  const duration = endTime ? endTime - startTime : Date.now() - startTime;
  const isActive = status === 'uploading';

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1200,
        width: expanded ? { xs: 'calc(100vw - 40px)', sm: 400 } : 'auto',
        maxWidth: expanded ? 400 : 'none'
      }}
    >
      <Card
        elevation={8}
        sx={{
          border: 2,
          borderColor: `${getStatusColor(status)}.main`,
          backgroundColor: 'background.paper',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            elevation: 12,
            transform: 'translateY(-1px)'
          }
        }}
        onClick={expanded ? undefined : handleToggleExpanded}
      >
        <CardContent sx={{
          pb: expanded ? 2 : 0.5,
          pt: expanded ? 2 : 1,
          px: expanded ? 2 : 1.5,
          minWidth: expanded ? 'auto' : '200px'
        }}>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={expanded ? 1 : 0.5}>
              {getStatusIcon(status)}
              <Box>
                <Typography
                  variant={expanded ? "subtitle2" : "caption"}
                  fontWeight="bold"
                  sx={{ fontSize: expanded ? undefined : '0.75rem' }}
                >
                  {status === 'uploading' && (uploadState?.reconnected ? 'Upload Continuing...' : 'Bulk Upload')}
                  {status === 'completed' && 'Upload Completed'}
                  {status === 'failed' && 'Upload Failed'}
                </Typography>
                {!expanded && status === 'uploading' && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                    {progress}% - Batch {currentBatch}/{totalBatches}
                  </Typography>
                )}
                {expanded && status === 'uploading' && uploadState?.reconnected && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    (Reconnected)
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" alignItems="center">
              {expanded && (
                <IconButton
                  size="small"
                  onClick={handleToggleExpanded}
                  sx={{ mr: 1 }}
                >
                  <CollapseIcon />
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ p: expanded ? 0.5 : 0.25 }}
              >
                <CloseIcon fontSize={expanded ? 'small' : 'inherit'} />
              </IconButton>
            </Box>
          </Box>

          {/* Progress Bar - only show when expanded */}
          {isActive && expanded && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="caption">
                  {progress}% Complete
                </Typography>
                <Typography variant="caption">
                  {uploadTracker.formatDuration(duration)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Compact progress bar when collapsed */}
          {isActive && !expanded && (
            <Box sx={{ mt: 0.5, width: '100%' }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 3, borderRadius: 1 }}
              />
            </Box>
          )}

          {/* Expanded Details */}
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2 }}>
              {/* Reconnection Notice */}
              {isActive && uploadState?.reconnected && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Upload Reconnected
                  </Typography>
                  <Typography variant="caption">
                    The upload is continuing in the background. Progress may not be accurate but the files are still being processed.
                  </Typography>
                </Alert>
              )}

              {/* Upload Info */}
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Document Type:
                  </Typography>
                  <Chip
                    label={documentType}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    Mode:
                  </Typography>
                  <Typography variant="caption">
                    {uploadMode === 'zip' ? 'ZIP Archive' :
                     totalBatches > 1 ? `Batch (${currentBatch}/${totalBatches})` :
                     'Direct Upload'}
                  </Typography>
                </Box>

                {totalFiles > 0 && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Files:
                    </Typography>
                    <Typography variant="caption">
                      {processedFiles || 0} / {totalFiles}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Results */}
              {results && status === 'completed' && (
                <Alert severity="success" sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Upload Successful
                  </Typography>
                  <Typography variant="caption">
                    {typeof results.successful === 'number' ? results.successful : results.successful?.length || 0} successful, {typeof results.failed === 'number' ? results.failed : results.failed?.length || 0} failed
                  </Typography>
                </Alert>
              )}

              {/* Error */}
              {error && status === 'failed' && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Upload Failed
                  </Typography>
                  <Typography variant="caption">
                    {error}
                  </Typography>
                </Alert>
              )}

              {/* Warnings */}
              {warnings && warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {warnings.length} Warning(s)
                  </Typography>
                </Alert>
              )}

              {/* Action Buttons */}
              {status === 'uploading' && uploadState?.reconnected && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button
                    size="small"
                    onClick={() => window.location.reload()}
                    variant="outlined"
                  >
                    Refresh Status
                  </Button>
                  <Button size="small" onClick={handleClose} sx={{ ml: 1 }}>
                    Dismiss
                  </Button>
                </Box>
              )}
              {status !== 'uploading' && (
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Button size="small" onClick={handleClose}>
                    Dismiss
                  </Button>
                  {status === 'completed' && (
                    <Button
                      size="small"
                      variant="text"
                      sx={{ ml: 1 }}
                      onClick={() => window.location.href = '/admin/bulk-upload'}
                    >
                      View Details
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GlobalUploadStatus;